import { useCallback, useEffect, useState } from 'react'
import Checkbox from '../checkbox/Checkbox'
import { AppScrollWrapperNew } from '../../widgets/AppScrollWrapper/AppScrollWrapper'
import AppBtn, { AppBtnColor, AppBtnSize, IconPosition } from '../btn/AppBtn'
import { AppNotification, NotificationType } from '../notification/Notification'
import moment from 'moment'

interface IConflictsPopupProps<T> {
  setActive: (value: boolean) => void
  setLoading: (value: boolean) => void
  successMessage: string
  updateData: () => [
    (arg: { data: T[] }) => Promise<any> & { unwrap: () => Promise<any> },
    { isLoading: boolean },
  ]
  conflictCount: number
  existingItems: T[]
  uploadedItems: T[]
  getItemName: (item: T) => string
  getItemGuid: (item: T) => string
  getItemUpdatedAt: (item: T) => string | Date
}
interface ISelectedItems {
  [key: number]: 'old' | 'new'
}
export const ConflictsBlock = <T,>({
  setActive,
  setLoading,
  successMessage,
  updateData,
  conflictCount,
  existingItems,
  uploadedItems,
  getItemName,
  getItemGuid,
  getItemUpdatedAt,
}: IConflictsPopupProps<T>) => {
  const [selectedItems, setSelectedItems] = useState<ISelectedItems | null>(
    null,
  )
  const [chooseAllOldData, setChooseAllOldData] = useState(false)
  const [chooseAllNewData, setChooseAllNewData] = useState(false)

  const [updateUploadedData, { isLoading }] = updateData()

  const outSideClickHandler = useCallback(() => {
    setActive(false)
  }, [])

  const onChooseItem = (index: number, param: 'old' | 'new') => {
    if (selectedItems && selectedItems[index] === param) return
    setSelectedItems((prev) => ({ ...prev, [index]: param }))
    if (param === 'old' && chooseAllNewData) {
      setChooseAllNewData(false)
    }
    if (param === 'new' && chooseAllOldData) {
      setChooseAllOldData(false)
    }
  }

  useEffect(() => {
    const result: ISelectedItems = {}
    for (let i: number = 0; i < conflictCount; i++) {
      result[i] = 'old'
    }
    setSelectedItems(result)
  }, [])

  useEffect(() => {
    setLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    if (
      !!selectedItems &&
      Object.entries(selectedItems).filter((item) => item[1] === 'old')
        .length === conflictCount &&
      !chooseAllOldData
    ) {
      setChooseAllOldData(true)
    }
    if (
      !!selectedItems &&
      Object.entries(selectedItems).filter((item) => item[1] === 'new')
        .length === conflictCount &&
      !chooseAllNewData
    ) {
      setChooseAllNewData(true)
    }
  }, [selectedItems])

  const onChooseAllItem = (value: boolean, param: 'old' | 'new') => {
    if (value) {
      const result: ISelectedItems = {}
      for (let i: number = 0; i < conflictCount; i++) {
        result[i] = param
      }
      setSelectedItems(result)
      if (param === 'old') {
        setChooseAllOldData(true)
        setChooseAllNewData(false)
      }
      if (param === 'new') {
        setChooseAllOldData(false)
        setChooseAllNewData(true)
      }
      return
    }
    setSelectedItems(null)
    setChooseAllOldData(false)
    setChooseAllNewData(false)
  }

  const saveData = () => {
    if (!selectedItems || Object.keys(selectedItems).length < conflictCount) {
      AppNotification({
        msg: 'Для продолжения должны быть отмечены все пункты!',
        type: NotificationType.error,
      })
      return
    }
    const newData = Object.entries(selectedItems).map(([key, value]) =>
      value === 'old' ? existingItems[Number(key)] : uploadedItems[Number(key)],
    ) as T[]

    updateUploadedData({ data: newData })
      .unwrap()
      .then(() => {
        if (
          selectedItems &&
          Object.entries(selectedItems).filter((item) => item[1] === 'old')
            .length !== conflictCount
        ) {
          AppNotification({
            msg: successMessage,
            type: NotificationType.success,
          })
        }
        setActive(false)
      })
  }

  return (
    <div className="popup-conflicts">
      <span className="total-conflicts">
        Всего конфликтных данных: {conflictCount}
      </span>
      <small className="bold">Какие данные вы хотите сохранить?</small>
      <span className="description-conflicts">
        Найдено {conflictCount} повторяющихся строк данных. Выберите файлы,
        которые необходимо сохранить
      </span>
      <div className="conflicts-checkbox-wrap">
        <div className="conflicts-checkbox-header">
          <Checkbox
            name={'8-pb-all-old'}
            value={chooseAllOldData}
            onChange={(value) => onChooseAllItem(value, 'old')}
            text="Существующие данные"
          />
          <Checkbox
            name={'8-pb-all-new'}
            value={chooseAllNewData}
            onChange={(value) => onChooseAllItem(value, 'new')}
            text="Загружаемые данные"
          />
        </div>
        <div className="conflicts-checkbox-body">
          <AppScrollWrapperNew theme="dropdown">
            {Array.from({ length: conflictCount }, (_, index) => index).map(
              (item, index) => {
                const existingItem = existingItems[index]
                const newItem = uploadedItems[index]
                return (
                  <div className="checkbox-row" key={`conflict-item-${index}`}>
                    <div className="old-data">
                      <Checkbox
                        name={getItemGuid(existingItem) + index + 'old'}
                        value={
                          !!selectedItems && selectedItems[index] === 'old'
                        }
                        onChange={() => onChooseItem(index, 'old')}
                        text={getItemName(existingItem)}
                      />
                      <small>
                        Изменение от{' '}
                        {moment(getItemUpdatedAt(existingItem)).format(
                          'DD.MM.YYYY',
                        )}
                      </small>
                    </div>
                    <div className="new-data pl-20">
                      <Checkbox
                        name={getItemGuid(newItem) + index + 'new'}
                        value={
                          !!selectedItems && selectedItems[index] === 'new'
                        }
                        onChange={() => onChooseItem(index, 'new')}
                        text={getItemName(newItem)}
                      />
                      <small>
                        Изменение от {moment(new Date()).format('DD.MM.YYYY')}
                      </small>
                    </div>
                  </div>
                )
              },
            )}
          </AppScrollWrapperNew>
        </div>
      </div>
      <div className="buttons-block mt-20">
        <AppBtn
          title="Отменить"
          color={AppBtnColor.outlineViolet}
          sized={AppBtnSize.md}
          onClick={outSideClickHandler}
          iconClassName="close"
        />
        <AppBtn
          title="Продолжить"
          color={AppBtnColor.violetWhite}
          sized={AppBtnSize.md}
          onClick={saveData}
          iconClassName="arrow-long"
          iconPosition={IconPosition.right}
        />
      </div>
    </div>
  )
}
